// SPDX-License-Identifier: AGPL-3.0-or-later

import type {FlatEmoji} from '@app/features/emoji/types/EmojiTypes';
import {EMOJI_MENU_CONFIG} from '@app/features/ui/action_menu/items/ExpressionMenuConfig';

// Fork imports.

import {EmojiPickerReactionContext} from '@app/features/channel/components/EmojiPicker';
import EmojiPicker, {getEmojiUsageKey} from '@app/features/emoji/state/EmojiPicker';
import EmojiPins from '@app/features/emoji/state/EmojiPins';
import ExpressionPicker from '@app/features/emoji/state/ExpressionPicker';
import Guilds from '@app/features/guild/state/Guilds';
import {LINK_COPIED_TO_CLIPBOARD_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import Drafts from '@app/features/messaging/state/MessagingDrafts';
import {createDownloadHandler} from '@app/features/messaging/utils/FileDownloadUtils';
import {PersonaReactAsModal, REACT_AS_DESCRIPTOR} from '@app/features/persona/components/PersonaReactAsModal';
import {type ClientPersona, PersonaStore} from '@app/features/persona/state/PersonaStore';
import {SubMenu as MenuItemSubmenu} from '@app/features/ui/action_menu/ContextMenu';
import {CopyLinkIcon, CopyMediaIcon, DownloadMediaIcon} from '@app/features/ui/action_menu/ContextMenuIcons';
import {CloneExpressionMenuItem} from '@app/features/ui/action_menu/items/CloneExpressionMenuItem';
import {copyMediaToClipboard} from '@app/features/ui/action_menu/items/MediaMenuData';
import styles from '@app/features/ui/action_menu/items/MenuItems.module.css';
import {ReverseImageSearchMenuItems} from '@app/features/ui/action_menu/items/ReverseImageSearchMenuItems';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {ClipboardIcon, PushPinIcon, PushPinSlashIcon, Smiley, StarIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback, useContext} from 'react';

const PIN_EMOJI_DESCRIPTOR = msg({
	message: 'Pin emoji',
	comment: 'Emoji context menu action that pins the emoji.',
});
const UNPIN_EMOJI_DESCRIPTOR = msg({
	message: 'Unpin emoji',
	comment: 'Emoji context menu action that unpins the emoji.',
});
const PIN_FOR_PERSONA_DESCRIPTOR = msg({
	message: 'Pin for this persona',
	comment: 'Emoji context menu action that pins the emoji for the active persona.',
});
const UNPIN_FOR_PERSONA_DESCRIPTOR = msg({
	message: 'Unpin for this persona',
	comment: 'Emoji context menu action that unpins the emoji for the active persona.',
});
const UNFAVORITE_EMOJI_DESCRIPTOR = msg({
	message: 'Unfavorite emoji',
	comment: 'Emoji context menu action that removes the emoji from favorites.',
});
const FAVORITE_EMOJI_DESCRIPTOR = msg({
	message: 'Favorite emoji',
	comment: 'Emoji context menu action that adds the emoji to favorites.',
});
const COPY_EMOJI_ID_DESCRIPTOR = msg({
	message: 'Copy emoji ID',
	comment: 'Developer-mode action that copies the emoji ID to the clipboard.',
});
const COPY_IMAGE_DESCRIPTOR = msg({
	message: 'Copy image',
	comment: 'Media context menu action that copies an image to the clipboard.',
});
const COPY_GIF_DESCRIPTOR = msg({
	message: 'Copy GIF',
	comment: 'Media context menu action that copies a GIF to the clipboard.',
});
const DOWNLOAD_IMAGE_DESCRIPTOR = msg({
	message: 'Download image',
	comment: 'Image context menu action that downloads the image to disk.',
});
const DOWNLOAD_GIF_DESCRIPTOR = msg({
	message: 'Download GIF',
	comment: 'Media context menu action that downloads a GIF to disk.',
});
const COPY_IMAGE_LINK_DESCRIPTOR = msg({
	message: 'Copy image link',
	comment: 'Image context menu action that copies the image URL to the clipboard.',
});
const COPY_GIF_LINK_DESCRIPTOR = msg({
	message: 'Copy GIF link',
	comment: 'Media context menu action that copies the URL of the GIF.',
});
const MORE_EMOJI_ACTIONS_DESCRIPTOR = msg({
	message: 'More emoji actions',
	comment: 'Submenu label that contains additional emoji context menu actions.',
});

interface EmojiContextMenuItemsProps {
	emoji: FlatEmoji;
	onClose: () => void;
	channelId?: string;
	messageId?: string;
}

const useEmojiHandlers = (emoji: FlatEmoji, onClose: () => void, activePersona?: ClientPersona | null) => {
	const {i18n} = useLingui();
	const canFavorite = !emoji.id || Boolean(emoji.guildId && Guilds.getGuild(emoji.guildId));
	const isFavorite = canFavorite ? EmojiPicker.isFavorite(emoji) : false;
	const isGloballyPinned = EmojiPins.isGloballyPinned(getEmojiUsageKey(emoji));
	const isPersonaPinned = activePersona ? EmojiPins.isPersonaPinned(getEmojiUsageKey(emoji), activePersona.id) : false;
	const originalUrl = emoji.id
		? AvatarUtils.getEmojiOriginalURL({id: emoji.id, animated: emoji.animated})
		: (emoji.url ?? null);

	const copyLabel = emoji.animated ? i18n._(COPY_GIF_DESCRIPTOR) : i18n._(COPY_IMAGE_DESCRIPTOR);
	const downloadLabel = emoji.animated ? i18n._(DOWNLOAD_GIF_DESCRIPTOR) : i18n._(DOWNLOAD_IMAGE_DESCRIPTOR);
	const copyLinkLabel = emoji.animated ? i18n._(COPY_GIF_LINK_DESCRIPTOR) : i18n._(COPY_IMAGE_LINK_DESCRIPTOR);

	const handleToggleFavorite = useCallback(() => {
		EmojiPicker.toggleFavorite(getEmojiUsageKey(emoji));
	}, [emoji]);

	const handleToggleGlobalPin = useCallback(() => {
		EmojiPins.togglePin(getEmojiUsageKey(emoji));
	}, [emoji]);

	const handleTogglePersonaPin = useCallback(() => {
		if (!activePersona) return;
		EmojiPins.togglePin(getEmojiUsageKey(emoji), activePersona.id);
	}, [emoji, activePersona]);

	const handleCopyId = useCallback(() => {
		if (!emoji.id) return;
		TextCopyCommands.copy(i18n, emoji.id);
		onClose();
	}, [i18n, emoji.id, onClose]);

	const handleCopyImage = useCallback(async () => {
		if (!originalUrl) return;
		const mediaType = emoji.animated ? 'gif' : 'image';
		const suggestedFilename = `${emoji.name}.${emoji.animated ? 'gif' : 'png'}`;
		await copyMediaToClipboard({
			i18n,
			originalSrc: originalUrl,
			type: mediaType,
			defaultName: suggestedFilename,
		});
		onClose();
	}, [emoji.animated, emoji.name, i18n, onClose, originalUrl]);

	const handleDownloadImage = useCallback(() => {
		if (!originalUrl) return;
		const mediaType = emoji.animated ? 'gif' : 'image';
		const suggestedFilename = `${emoji.name}.${emoji.animated ? 'gif' : 'png'}`;
		createDownloadHandler(originalUrl, mediaType, suggestedFilename)();
		onClose();
	}, [emoji.animated, emoji.name, onClose, originalUrl]);

	const handleCopyUrl = useCallback(async () => {
		if (!originalUrl) return;
		await TextCopyCommands.copy(i18n, originalUrl, true);
		ToastCommands.createToast({
			type: 'success',
			children: i18n._(LINK_COPIED_TO_CLIPBOARD_DESCRIPTOR),
		});
		onClose();
	}, [i18n, onClose, originalUrl]);

	return {
		canFavorite,
		isFavorite,
		isGloballyPinned,
		isPersonaPinned,
		originalUrl,
		copyLabel,
		downloadLabel,
		copyLinkLabel,
		handleToggleFavorite,
		handleToggleGlobalPin,
		handleTogglePersonaPin,
		handleCopyId,
		handleCopyImage,
		handleDownloadImage,
		handleCopyUrl,
	};
};

// One layout for every emoji menu: the picker, the reactions window, reactions on a message and emoji in message text.
export const EmojiContextMenuItems = observer(
	({emoji, onClose, channelId: propChannelId, messageId: propMessageId}: EmojiContextMenuItemsProps) => {
		const {i18n} = useLingui();
		const reactionContext = useContext(EmojiPickerReactionContext);
		const resolvedChannelId = propChannelId ?? reactionContext?.channelId ?? ExpressionPicker.channelId ?? undefined;
		// "React as..." needs an explicit target; the picker's channel only selects the persona shown for pinning.
		const reactChannelId = propChannelId ?? reactionContext?.channelId;
		const reactMessageId = propMessageId ?? reactionContext?.messageId;

		const hasPersonas = PersonaStore.personas.length > 0;
		const canReactAs = hasPersonas && Boolean(reactChannelId && reactMessageId);
		const draft = resolvedChannelId ? Drafts.getDraft(resolvedChannelId) : undefined;
		const activePersona = PersonaStore.getEffectiveReactionPersona(resolvedChannelId ?? null, null, draft);

		const handleReactAs = useCallback(() => {
			if (!reactChannelId || !reactMessageId) return;
			onClose();
			ModalCommands.push(
				modal(() => (
					<PersonaReactAsModal
						emoji={emoji}
						channelId={reactChannelId}
						messageId={reactMessageId}
						onClose={() => ModalCommands.pop()}
						data-flx="ui.action-menu.items.emoji-context-menu-items.handle-react-as.persona-react-as-modal"
					/>
				)),
			);
		}, [emoji, reactChannelId, reactMessageId, onClose]);

		const {
			canFavorite,
			isFavorite,
			isGloballyPinned,
			isPersonaPinned,
			originalUrl,
			copyLabel,
			downloadLabel,
			copyLinkLabel,
			handleToggleFavorite,
			handleToggleGlobalPin,
			handleTogglePersonaPin,
			handleCopyId,
			handleCopyImage,
			handleDownloadImage,
			handleCopyUrl,
		} = useEmojiHandlers(emoji, onClose, activePersona);

		const showSubmenu = Boolean(emoji.id) || Boolean(originalUrl);

		return (
			<>
				{canReactAs && (
					<MenuGroup data-flx="ui.action-menu.items.emoji-context-menu-items.react-as-menu-group">
						<MenuItem
							icon={
								<Smiley
									className={styles.iconSmall}
									weight="bold"
									data-flx="ui.action-menu.items.emoji-context-menu-items.react-as-icon"
								/>
							}
							onClick={handleReactAs}
							data-flx="ui.action-menu.items.emoji-context-menu-items.menu-item.react-as"
						>
							{i18n._(REACT_AS_DESCRIPTOR)}
						</MenuItem>
					</MenuGroup>
				)}
				<MenuGroup data-flx="ui.action-menu.items.emoji-context-menu-items.menu-group">
					{originalUrl && (
						<MenuItem
							icon={
								<DownloadMediaIcon
									size={20}
									data-flx="ui.action-menu.items.emoji-context-menu-items.download-media-icon"
								/>
							}
							onClick={handleDownloadImage}
							data-flx="ui.action-menu.items.emoji-context-menu-items.menu-item.download-image"
						>
							{downloadLabel}
						</MenuItem>
					)}
					<MenuItem
						icon={
							isGloballyPinned ? (
								<PushPinSlashIcon
									className={styles.iconSmall}
									weight="bold"
									data-flx="ui.action-menu.items.emoji-context-menu-items.icon-unpin"
								/>
							) : (
								<PushPinIcon
									className={styles.iconSmall}
									weight="bold"
									data-flx="ui.action-menu.items.emoji-context-menu-items.icon-pin"
								/>
							)
						}
						onClick={handleToggleGlobalPin}
						data-flx="ui.action-menu.items.emoji-context-menu-items.menu-item.toggle-pin"
					>
						{isGloballyPinned ? i18n._(UNPIN_EMOJI_DESCRIPTOR) : i18n._(PIN_EMOJI_DESCRIPTOR)}
					</MenuItem>
					{hasPersonas && activePersona && (
						<MenuItem
							icon={
								isPersonaPinned ? (
									<PushPinSlashIcon
										className={styles.iconSmall}
										weight="bold"
										data-flx="ui.action-menu.items.emoji-context-menu-items.icon-unpin-persona"
									/>
								) : (
									<PushPinIcon
										className={styles.iconSmall}
										weight="bold"
										data-flx="ui.action-menu.items.emoji-context-menu-items.icon-pin-persona"
									/>
								)
							}
							onClick={handleTogglePersonaPin}
							data-flx="ui.action-menu.items.emoji-context-menu-items.menu-item.toggle-pin-persona"
						>
							{isPersonaPinned ? i18n._(UNPIN_FOR_PERSONA_DESCRIPTOR) : i18n._(PIN_FOR_PERSONA_DESCRIPTOR)}
						</MenuItem>
					)}
					{canFavorite && (
						<MenuItem
							icon={
								<StarIcon
									className={styles.iconSmall}
									weight={isFavorite ? 'fill' : 'bold'}
									data-flx="ui.action-menu.items.emoji-context-menu-items.icon-small"
								/>
							}
							onClick={handleToggleFavorite}
							data-flx="ui.action-menu.items.emoji-context-menu-items.menu-item.toggle-favorite"
						>
							{isFavorite ? i18n._(UNFAVORITE_EMOJI_DESCRIPTOR) : i18n._(FAVORITE_EMOJI_DESCRIPTOR)}
						</MenuItem>
					)}
					{showSubmenu && (
						<MenuItemSubmenu
							label={i18n._(MORE_EMOJI_ACTIONS_DESCRIPTOR)}
							render={() => (
								<>
									{originalUrl && (
										<MenuGroup data-flx="ui.action-menu.items.emoji-context-menu-items.media-menu-group">
											<MenuItem
												icon={
													<CopyMediaIcon
														size={20}
														data-flx="ui.action-menu.items.emoji-context-menu-items.copy-media-icon"
													/>
												}
												onClick={handleCopyImage}
												data-flx="ui.action-menu.items.emoji-context-menu-items.menu-item.copy-image"
											>
												{copyLabel}
											</MenuItem>
											<MenuItem
												icon={
													<CopyLinkIcon
														size={20}
														data-flx="ui.action-menu.items.emoji-context-menu-items.copy-link-icon"
													/>
												}
												onClick={handleCopyUrl}
												data-flx="ui.action-menu.items.emoji-context-menu-items.menu-item.copy-link"
											>
												{copyLinkLabel}
											</MenuItem>
										</MenuGroup>
									)}
									{emoji.id && (
										<MenuGroup data-flx="ui.action-menu.items.emoji-context-menu-items.id-menu-group">
											<CloneExpressionMenuItem
												item={emoji}
												config={EMOJI_MENU_CONFIG.clone}
												onClose={onClose}
												data-flx="ui.action-menu.items.emoji-context-menu-items.clone-emoji-menu-item"
											/>
											<MenuItem
												icon={
													<ClipboardIcon
														className={styles.iconSmall}
														data-flx="ui.action-menu.items.emoji-context-menu-items.icon-small--2"
													/>
												}
												onClick={handleCopyId}
												data-flx="ui.action-menu.items.emoji-context-menu-items.menu-item.copy-id"
											>
												{i18n._(COPY_EMOJI_ID_DESCRIPTOR)}
											</MenuItem>
										</MenuGroup>
									)}
									{originalUrl && (
										<ReverseImageSearchMenuItems
											imageUrl={originalUrl}
											onClose={onClose}
											includeCopyAndOpen={false}
											data-flx="ui.action-menu.items.emoji-context-menu-items.reverse-image-search-menu-items"
										/>
									)}
								</>
							)}
							data-flx="ui.action-menu.items.emoji-context-menu-items.menu-item-submenu"
						/>
					)}
				</MenuGroup>
			</>
		);
	},
);

EmojiContextMenuItems.displayName = 'EmojiContextMenuItems';

// Upstream's name for the emoji items shown inside the message menu.
export const EmojiInlineMenuItems = EmojiContextMenuItems;
